import parsePhoneNumberFromString from 'libphonenumber-js';
import * as Yup from 'yup';

const phoneRegExp = /^(\+1|1)?(\d{10})$/;

// Step 1 validation schema - basic info
export const basicInfoValidationSchema = Yup.object().shape({
  fname: Yup.string()
    .required('First name is required')
    .min(3, 'Minimum 3 characters are required'),
  lname: Yup.string()
    .required('Last name is required')
    .min(3, 'Minimum 3 characters are required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  phone: Yup.string()
    .required('Phone number is required')
    .test('is-valid-phone', 'Phone number is not valid', function (value) {
      const { countrySymbol } = this.parent;
      if (!value || !countrySymbol || value.length < 7) return false;
      const phoneNumber = parsePhoneNumberFromString(value, countrySymbol);
      return phoneNumber && phoneNumber.isPossible() && phoneNumber.isValid();
    }),
  password: Yup.string()
    .required('Password is required')
    .min(8)
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/,
      'Password too weak',
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/,
      'Password too weak',
    )
    .required('Repeat password is required'),
});

// Step 2 validation schema - address info (base schema without phone)
const baseAddressValidationSchema = Yup.object().shape({
  location: Yup.object().shape({
    addressLine1: Yup.string()
      .required('Address line 1 is required')
      .min(5, 'Please enter a valid address'),
    addressLine2: Yup.string(), // Optional field
    city: Yup.string()
      .required('City is required')
      .min(2, 'Please enter a valid city'),
    state: Yup.string().required('State is required'),
    zip: Yup.string()
      .required('Postal code is required')
      .matches(/^\d{5}(-\d{4})?$/, 'Please enter a valid postal code'),
  }),
});

// Function to get address validation schema with conditional phone validation
export const getAddressValidationSchema = (isGoogleSignup: boolean = false) => {
  if (isGoogleSignup) {
    return baseAddressValidationSchema.shape({
      phone: Yup.string()
        .required('Phone number is required')
        .test('is-valid-phone', 'Phone number is not valid', function (value) {
          const { countrySymbol } = this.parent;
          if (!value || !countrySymbol || value.length < 7) return false;
          const phoneNumber = parsePhoneNumberFromString(value, countrySymbol);
          return (
            phoneNumber && phoneNumber.isPossible() && phoneNumber.isValid()
          );
        }),
    });
  }
  return baseAddressValidationSchema;
};

// Legacy export for backward compatibility
export const addressValidationSchema = baseAddressValidationSchema;

// Combined validation schema for final submission
export const getValidationSchema = (isGoogleSignup: boolean = false) => {
  return basicInfoValidationSchema.concat(
    getAddressValidationSchema(isGoogleSignup),
  );
};

// Legacy export for backward compatibility
export const validationSchema = basicInfoValidationSchema.concat(
  addressValidationSchema,
);

export const initialValues = {
  fname: '',
  lname: '',
  email: '',
  phone: '',
  referralCode: '',
  countryCode: '+1',
  countrySymbol: 'US',
  password: '',
  confirmPassword: '',
  location: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
  },
  idToken: undefined,
};
